import { Fragment, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { aud } from '@/utils/format';
import {
  artistArrearsAud,
  artistOutstanding,
  dayExpectedPayouts,
  dayPayouts,
  deferPayout,
  isCommitted,
  cashLedgerFor,
  payableBundle,
  sendPayouts,
  settlePayouts,
  recordFloatMovement,
  type DayPayout,
  type PayableBundle,
} from '@/domain/payoutFlow';
import { dayKey } from '@/utils/studioDay';
import { format } from 'date-fns';
import { dayCoverTimeline, projectedCashByTime, projectedDayMoney, recommendPaymentMethod } from '@/domain/cashFlow';
import { bookingPriceAud } from '@/domain/pricing';
import type { Booking, StudioId } from '@/domain/types';
import { arrangementLabel, finishTime } from '@/screens/payouts/payoutLabels';
import SteeringExplainer from '@/components/floor/SteeringExplainer';
import Workings, { type WorkingsLine } from '@/components/Workings';

/** The summary tiles, in reading order: the day, then where its money lands. */
type TileKey = 'booked' | 'underway' | 'artists' | 'cash' | 'bank' | 'drawer';

const WORKINGS_TITLE: Record<TileKey, string> = {
  booked: 'Booked today — how it adds up',
  underway: 'In chair + completed — what has actually happened',
  artists: 'To artists — how the commission is worked out',
  cash: 'Predicted cash — what we expect in the drawer',
  bank: 'Predicted bank — where the rest of the day goes',
  drawer: 'Cash in hand — the drawer right now, and at close',
};

interface Props {
  studioId: StudioId;
  studioName: string;
  dayIso: string;
}

/**
 * State + colour for an artist's payout row / timeline node. Reads the artist's
 * committed bookings for the day so an arriving / in-chair appointment reads its
 * real state rather than falling through to a "still in chair" catch-all: a
 * confirmed client who has not walked in yet is "Arriving", not in the chair.
 */
function payoutState(
  p: DayPayout,
  committedBookings: Booking[],
): { label: string; dot: string; pill: string } {
  if (p.record?.status === 'signed') {
    return { label: `Signed · ${finishTime(p.record.signedAt)}`, dot: 'bg-mint', pill: 'pill-mint' };
  }
  if (p.record?.status === 'deferred') {
    return {
      label: `Deferred · nothing paid · ${aud(p.record.amountDueAud)} owed`,
      dot: 'bg-blood',
      pill: 'pill-blood',
    };
  }
  if (p.record?.status === 'sent') {
    return { label: 'Awaiting artist', dot: 'bg-gold-dim', pill: 'pill-cream' };
  }
  if (committedBookings.some((b) => b.status === 'in-chair')) {
    return { label: 'In chair', dot: 'bg-gold-dim', pill: 'pill-gold' };
  }
  if (committedBookings.length > 0 && committedBookings.every((b) => b.status === 'completed')) {
    // All sessions done. L5: the sale may not be rung up yet (transient), then the
    // envelope is payable at the last session's finish (not the rostered shift end).
    if (!p.saleSettled) {
      return { label: 'Sale pending', dot: 'bg-gold-dim', pill: 'pill-cream' };
    }
    return { label: `Payable now · finished ${finishTime(p.finishIso)}`, dot: 'bg-gold-bright', pill: 'pill-gold' };
  }
  // Confirmed / checked-in only: booked in and expected, not yet in the chair.
  return { label: `Arriving · expected ${finishTime(p.expectedFinishIso)}`, dot: 'bg-gold-dim', pill: 'pill-cream' };
}

export default function PayoutCashPanel({ studioId, studioName, dayIso }: Props) {
  // Subscribe to the slices the derivations read, so the panel re-renders when a
  // sale or a sign-off lands. The maths itself stays in payoutFlow (READ, DON'T
  // RECOMPUTE) — we only read it back here.
  const bookings = useBrainStore((s) => s.bookings);
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const cashLedgers = useBrainStore((s) => s.cashLedgers);
  const artists = useBrainStore((s) => s.artists);
  const salePayments = useBrainStore((s) => s.salePayments);
  const deposits = useBrainStore((s) => s.deposits);

  const [openTile, setOpenTile] = useState<TileKey | null>(null);
  const [floatOpen, setFloatOpen] = useState(false);
  const [floatDraft, setFloatDraft] = useState('');
  const [floatReason, setFloatReason] = useState('');

  // The COMMITTED appointment set (confirmed / checked-in / in-chair /
  // completed), each with its expected envelope - so arriving clients (like
  // Indi) show up in the list, signable only once every session is completed.
  const payouts = useMemo(
    () => dayExpectedPayouts(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, bookings, payoutRecords, artists],
  );
  // Each artist's committed bookings for the day, so a row can read its real
  // state (arriving / in chair / payable) rather than a payable flag alone.
  const committedByArtist = useMemo(() => {
    const day = dayIso.slice(0, 10);
    const map = new Map<string, Booking[]>();
    for (const b of Object.values(bookings)) {
      if (b.studioId !== studioId || b.start.slice(0, 10) !== day || !isCommitted(b)) continue;
      map.set(b.artistId, [...(map.get(b.artistId) ?? []), b]);
    }
    return map;
  }, [bookings, studioId, dayIso]);
  // Time-phased green/red cover verdict (D7) - reads salePayments (cash actually
  // landed) and payoutRecords (cash already handed out) on top of the same
  // bookings/artists the envelopes are derived from, so a sign or a sale
  // recomputes the cards. READ-only: the maths lives in dayCoverTimeline.
  const cover = useMemo(
    () => dayCoverTimeline(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, salePayments, payoutRecords, bookings, artists],
  );
  const ledger = useMemo(
    () => cashLedgerFor(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, cashLedgers],
  );
  // The system's conclusion, not raw inputs: whether today's takings are on
  // track to cover the remaining envelopes, naming the next finisher.
  const steer = useMemo(
    () => recommendPaymentMethod(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, bookings, payoutRecords, cashLedgers, artists],
  );

  const working = ledger.cashInAud - ledger.payoutsAud;
  const physicalInTill = ledger.openingFloatAud + working;

  // Every committed booking today (confirmed / checked-in / in-chair /
  // completed), flattened from committedByArtist - the same roster the payout
  // rows read, so the tiles below can never disagree with the cards.
  const committedBookings = useMemo(
    () => [...committedByArtist.values()].flat(),
    [committedByArtist],
  );

  // Summary strip (27 Jul): the day, then where its money lands - booked, what
  // is under way or done, what that owes artists, and the cash / bank / drawer
  // split that conserves it. Every tile opens its own derivation, so none of
  // these figures has to be taken on trust.
  // "In chair + completed" - takings, narrowed to sessions actually under way or
  // finished (not still arriving/checked-in).
  const inChairOrCompletedAud = committedBookings
    .filter((b) => b.status === 'in-chair' || b.status === 'completed')
    .reduce((sum, b) => {
      return sum + bookingPriceAud(b, artists[b.artistId]);
    }, 0);
  // "To artists" - commission on the work that has actually happened (client
  // call, 27 Jul). Reads `dayPayouts` (in-chair + completed), NOT the committed
  // set: the tile answers "what have we earned the artists so far", so it moves
  // with the chairs rather than with the bookings diary. The whole day's
  // expected envelopes are still shown, in this tile's workings and in the
  // drawer maths, as `money.toArtistsDayAud`.
  const actualPayouts = useMemo(
    () => dayPayouts(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, bookings, payoutRecords, artists],
  );
  const toArtistsAud = actualPayouts.reduce((sum, p) => sum + p.envelopeAud, 0);
  // "Cash left after payouts" - the projected 6pm close position: the studio
  // front-loads cash to pay artists in cash, so the drawer should net to
  // roughly zero (excess revenue goes to card/bank), not the studio's margin.
  // The conservation view: where every dollar of the day lands. Cash alone left
  // the drawer netting to ~zero with no visible explanation of where the rest
  // went — the bank leg is what closes the loop.
  const money = useMemo(
    () => projectedDayMoney(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, bookings, salePayments, artists, deposits, payoutRecords, cashLedgers],
  );
  // Artists finishing before 6pm are paid at their own clock-off; the rest are
  // settled together at the 6pm clock-off (when every remaining client pays up).
  // Mark where that 6pm group starts so we can drop a divider before it.
  const firstClampedIdx = cover.findIndex(
    (c) => payouts.find((p) => p.artistId === c.artistId)?.clampedToClockoff,
  );

  // Which tile has its workings open. One at a time — the panel is the reading
  // surface, and two open chains invite comparing figures that answer different
  // questions (the mistake the old strip made).
  const toggleTile = (tile: TileKey) => setOpenTile((cur) => (cur === tile ? null : tile));

  // What the drawer can settle for one artist right now, in whole days (arrears
  // first). The send controls read this rather than deciding for themselves, so
  // what the button offers and what `settlePayouts` will accept cannot diverge.
  const bundleFor = (artistId: string): PayableBundle => payableBundle(studioId, artistId, dayIso);

  const sessionsDone = committedBookings.filter((b) => b.status === 'completed').length;
  const sessionsInChair = committedBookings.filter((b) => b.status === 'in-chair').length;
  const sessionsWaiting = committedBookings.length - sessionsDone - sessionsInChair;
  const ticket = committedBookings.length > 0 ? money.bookedAud / committedBookings.length : 0;
  const uniform = committedBookings.every(
    (b) => bookingPriceAud(b, artists[b.artistId]) === ticket,
  );
  /** "22 × $1,000" when every appointment is the same price, else just the count. */
  const bookedFormula = uniform
    ? `${committedBookings.length} × ${aud(ticket)}`
    : `${committedBookings.length} appointments`;

  /**
   * The time-phased half of the maths. A day's totals are only half the story:
   * the envelopes fall due at different moments — Pat at 2:30pm, Marlowe not
   * until the 6pm clock-off — and the cash has to be in the drawer as each
   * artist walks out, not merely by the end of the day. This ladder is why the
   * studio front-loads cash onto the early clients at all.
   *
   * Reads `dayCoverTimeline`, which already walks the artists in finish order
   * and carries the running total owed by each finish. Never recomputed here.
   */
  const clockLadder = (): WorkingsLine[] =>
    cover
      .filter((c) => !c.signed)
      .map((c): WorkingsLine => {
        const p = payouts.find((x) => x.artistId === c.artistId);
        const when = c.shiftEndIso ? finishTime(c.shiftEndIso) : 'clock-off';
        // Cash expected by THIS finish, on the same steer the tiles are built
        // from, so the ladder's last rung lands on the day's predicted cash
        // rather than on the all-clients-pay-cash ceiling.
        const cashByThen = c.shiftEndIso
          ? projectedCashByTime(studioId, dayIso, c.shiftEndIso)
          : money.cashAud;
        const short = Math.max(0, c.cumulativeDueAud - cashByThen);
        return {
          label: `${when} · ${c.artistName}${p?.clampedToClockoff ? ' (evening)' : ''}`,
          formula: `${aud(c.outstandingAud)} envelope · ${aud(c.cumulativeDueAud)} owed by then`,
          value: short === 0 ? `${aud(cashByThen)} in hand` : `short ${aud(short)}`,
          tone: short === 0 ? 'good' : 'bad',
          indent: true,
        };
      });

  /**
   * Days carried from earlier, per artist. Its own block in the workings rather
   * than folded into today's commission: arrears are not what today earned, but
   * they do come out of today's drawer, so the reader has to see both.
   */
  const arrearsLines = (): WorkingsLine[] => {
    if (money.arrearsAud <= 0) return [];
    const rows = payouts
      .map((p) => ({ name: p.artistName, arrears: artistArrearsAud(studioId, p.artistId, dayIso) }))
      .filter((r) => r.arrears > 0);
    return [
      {
        label: 'Owed from earlier days',
        formula: 'envelopes the takings could not cover, carried whole',
        value: aud(money.arrearsAud),
        rule: true,
        tone: 'bad',
      },
      ...rows.map((r): WorkingsLine => ({ label: r.name, value: aud(r.arrears), indent: true, tone: 'muted' })),
    ];
  };

  const workingsFor = (tile: TileKey): { lines: WorkingsLine[]; note?: string; check?: string } => {
    switch (tile) {
      case 'booked':
        return {
          lines: [
            { label: 'Every committed appointment', formula: bookedFormula, value: aud(money.bookedAud), tone: 'total' },
            { label: 'Completed', formula: `${sessionsDone} sessions`, value: aud(sessionsDone * ticket), indent: true, tone: 'muted' },
            { label: 'In chair', formula: `${sessionsInChair} sessions`, value: aud(sessionsInChair * ticket), indent: true, tone: 'muted' },
            { label: 'Still to come', formula: `${sessionsWaiting} sessions`, value: aud(sessionsWaiting * ticket), indent: true, tone: 'muted' },
            { label: 'Deposits already taken', formula: 'banked before today', value: aud(money.depositsBankedAud), rule: true },
            { label: 'Balances to collect today', value: aud(money.balancesAud), tone: 'total' },
          ],
          note: 'The day’s gross across confirmed, checked-in, in-chair and completed appointments. Merch is excluded — the studio keeps merch whole, it never splits.',
        };
      case 'underway':
        return {
          lines: [
            { label: 'Completed', formula: `${sessionsDone} × ${aud(ticket)}`, value: aud(sessionsDone * ticket), tone: 'muted' },
            { label: 'In chair', formula: `${sessionsInChair} × ${aud(ticket)}`, value: aud(sessionsInChair * ticket), tone: 'muted' },
            { label: 'Takings so far', value: aud(inChairOrCompletedAud), tone: 'total', rule: true },
            { label: 'Still to come', formula: `${sessionsWaiting} waiting`, value: aud(sessionsWaiting * ticket), tone: 'muted' },
          ],
          note: 'Work that has actually happened or is happening right now. Clients still waiting are booked, not earned.',
        };
      case 'artists':
        return {
          lines: [
            { label: 'Takings under way or done', formula: `${sessionsDone + sessionsInChair} × ${aud(ticket)}`, value: aud(inChairOrCompletedAud) },
            { label: 'Commission', formula: 'every artist on 50%', value: '× 0.50', tone: 'muted' },
            { label: 'Owed to artists on work so far', value: aud(toArtistsAud), tone: 'total', rule: true },
            ...actualPayouts.map((p): WorkingsLine => {
              // The artist's real clock-off comes off the COMMITTED row: this
              // row's own finish is only the end of the sessions counted here,
              // so it would read "Brandon finishes 2:30pm" while the ladder
              // below correctly says 4:00pm. One artist, one finish time.
              const finish = payouts.find((x) => x.artistId === p.artistId)?.expectedFinishIso;
              return {
                label: p.artistName,
                formula: `${p.sessionCount} × ${aud(ticket)} = ${aud(p.takingsAud)}${
                  finish ? ` · finishes ${finishTime(finish)}` : ''
                }`,
                value: aud(p.envelopeAud),
                indent: true,
                tone: 'muted',
              };
            }),
            { label: 'The whole day, once everyone is through', formula: 'all committed × 50%', value: aud(money.toArtistsDayAud), rule: true, tone: 'muted' },
            ...arrearsLines(),
            { label: 'When each envelope falls due', formula: 'the full day’s envelope, owed at that artist’s finish', rule: true },
            ...clockLadder(),
          ],
          note: 'Commission is charged on the session price, never on the cash collected — a deposit paid earlier does not shrink the artist’s envelope. An artist is paid at their own finish, not at the end of the day, so the envelopes come due in the order above.',
        };
      case 'cash':
        return {
          lines: [
            { label: 'Cash already in the till', formula: 'finished cash sales', value: aud(money.cashCollectedAud) },
            { label: 'Balances we will ask for in cash', formula: 'the front-load steer', value: aud(money.cashPendingAud) },
            { label: 'Predicted cash by close', value: aud(money.cashAud), tone: 'total', rule: true },
            { label: 'Envelopes to pay out in cash', value: `− ${aud(money.toArtistsDayAud)}`, tone: 'muted' },
            ...(money.arrearsAud > 0
              ? [
                  {
                    label: 'Carried from earlier days',
                    formula: 'paid whole out of today’s drawer',
                    value: `− ${aud(money.arrearsAud)}`,
                    tone: 'bad' as const,
                  },
                ]
              : []),
            {
              label: 'Cash surplus at close',
              value: aud(money.drawerSurplusAud),
              tone: money.drawerSurplusAud >= 0 ? 'good' : 'bad',
              rule: true,
            },
            { label: 'Cash has to be there as each artist walks out', rule: true },
            ...clockLadder(),
          ],
          note: 'Artists are paid in cash at their finish time, so a whole-day total is not enough — the drawer has to hold each envelope by that artist’s finish. That is why the studio front-loads cash onto the day’s earliest clients until every finish is covered; everyone after that can pay by card.',
        };
      case 'bank':
        return {
          lines: [
            { label: 'Deposits already banked', formula: 'taken before today', value: aud(money.depositsBankedAud) },
            { label: 'Card taken so far', value: aud(money.bankCollectedAud) },
            { label: 'Balances heading to card', formula: 'once the envelopes are covered', value: aud(money.bankPendingAud) },
            { label: 'Predicted bank', value: aud(money.bankAud), tone: 'total', rule: true },
          ],
          check: `${aud(money.cashAud)} cash + ${aud(money.bankAud)} bank = ${aud(money.bookedAud)} booked`,
          note: 'This is where the rest of the day goes. Without it the drawer nets to roughly zero and the money looks like it vanished — it did not, it settled to the bank.',
        };
      case 'drawer':
        return {
          lines: [
            { label: 'Opening float', formula: 'sealed, not spending money', value: aud(ledger.openingFloatAud) },
            { label: 'Cash taken so far', value: `+ ${aud(ledger.cashInAud)}` },
            { label: 'Paid out to artists', value: `− ${aud(ledger.payoutsAud)}` },
            { label: 'In the drawer right now', value: aud(physicalInTill), tone: 'total', rule: true },
            { label: 'Working cash', formula: 'excludes the float', value: aud(working), tone: 'muted' },
            {
              label: 'Still owed',
              formula: money.arrearsAud > 0 ? 'today’s envelopes plus what is carried' : 'today’s envelopes',
              value: aud(money.toArtistsDayAud + money.arrearsAud - ledger.payoutsAud),
              tone: 'muted',
            },
            {
              label: 'Projected at the 6pm close',
              formula: 'float + all cash − all envelopes',
              value: aud(money.drawerAtCloseAud),
              rule: true,
              tone: money.drawerAtCloseAud >= ledger.openingFloatAud ? 'good' : 'bad',
            },
          ],
          check: `${aud(money.bankAud)} bank + ${aud(money.drawerSurplusAud)} drawer surplus = ${aud(money.studioShareAud)} the studio keeps`,
          note: 'The float is a sealed emergency reserve, so it is excluded from the steering maths and stays behind at close.',
        };
    }
  };

  const floatAmount = Number(floatDraft);
  const floatAmountValid = Number.isFinite(floatAmount) && floatAmount > 0;
  // Every float move - infusion or withdrawal - must say why the cash moved
  // (audit trail); D9 closes the gap where only a withdrawal required one.
  const canMove = floatAmountValid && floatReason.trim() !== '';
  const moveFloat = (direction: 1 | -1) => {
    if (!canMove) return;
    recordFloatMovement(
      studioId,
      dayIso,
      direction > 0 ? 'infusion' : 'withdrawal',
      direction * Math.round(floatAmount),
      floatReason.trim() || undefined,
    );
    setFloatDraft('');
    setFloatReason('');
  };

  return (
    <div data-testid="payout-cash-panel">
      {/* Day-at-a-glance summary strip, above the per-artist detail: a
          reconciled CASH story - the cash we predict to collect, how much is
          under way or done, what's owed out to artists, and what's left in the
          drawer once they're paid (predicted cash - to artists = cash left). */}
      {payouts.length > 0 ? (
        <div className="mb-4" data-testid="payout-summary-block">
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-gold-dim/30 border border-gold-dim/30 rounded-md overflow-hidden"
            data-testid="payout-summary"
          >
            <SummaryTile
              label="Booked today"
              value={aud(money.bookedAud)}
              note={`${committedBookings.length} appointments`}
              testId="tile-booked"
              open={openTile === 'booked'}
              onToggle={() => toggleTile('booked')}
            />
            <SummaryTile
              label="In chair + completed"
              value={aud(inChairOrCompletedAud)}
              note="under way or done"
              testId="tile-underway"
              open={openTile === 'underway'}
              onToggle={() => toggleTile('underway')}
            />
            <SummaryTile
              label="To artists"
              value={aud(toArtistsAud)}
              note="commission on work done"
              testId="tile-artists"
              open={openTile === 'artists'}
              onToggle={() => toggleTile('artists')}
            />
            <SummaryTile
              label="Predicted cash"
              value={aud(money.cashAud)}
              note="taken + what we ask for"
              testId="tile-cash"
              open={openTile === 'cash'}
              onToggle={() => toggleTile('cash')}
            />
            <SummaryTile
              label="Predicted bank"
              value={aud(money.bankAud)}
              note="deposits + card"
              testId="tile-bank"
              open={openTile === 'bank'}
              onToggle={() => toggleTile('bank')}
            />
            <SummaryTile
              label="Cash in hand"
              value={aud(physicalInTill)}
              note="in the drawer now"
              tone={working >= 0 ? 'text-mint' : 'text-blood'}
              testId="tile-drawer"
              open={openTile === 'drawer'}
              onToggle={() => toggleTile('drawer')}
            />
          </div>

          {/* The middle step: how the tile above was worked out. Opens below the
              strip rather than inside a cell so the six-across grid never jumps. */}
          {openTile ? (
            <div
              className="mt-px rounded-b-md border border-t-0 border-gold-dim/30 bg-ink-dark/40 p-3.5"
              data-testid={`workings-${openTile}`}
            >
              <p className="label-small-caps text-gold-bright mb-2">
                {WORKINGS_TITLE[openTile]}
              </p>
              <Workings testId={`workings-body-${openTile}`} {...workingsFor(openTile)} />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* The system's steer — the actionable conclusion. The manager reads this,
          not the raw finish times / cash figures it's computed from. */}
      {payouts.length > 0 ? (
        <div
          className={clsx(
            'rounded-md border p-3 mb-4',
            steer.method === 'cash' ? 'border-blood/45 bg-blood/5' : 'border-mint/40 bg-mint/5',
          )}
          data-testid="steer-banner"
        >
          <div className="flex items-center gap-2">
            <span className={clsx('text-[10px]', steer.method === 'cash' ? 'pill-blood' : 'pill-mint')}>
              {steer.method === 'cash' ? 'Ask for cash' : 'Card is fine'}
            </span>
            <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
              Cash steer
            </span>
          </div>
          <p className="font-serif text-cream-soft text-sm mt-1.5">{steer.reasoning}</p>
          <SteeringExplainer studioId={studioId} dayIso={dayIso} steer={steer} />
        </div>
      ) : null}

      {/* Finish timeline removed (WS3): it duplicated the finish times the cards
          below already carry. Cards are finish-ordered, then by envelope $ desc
          within the 6pm clock-off group, and clamped artists carry a "6pm" tag. */}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* Artist payouts */}
        <div className="panel p-4 lg:col-span-3 space-y-2.5">
          <p className="label-small-caps text-gold-bright">Artist payouts · today</p>
          {payouts.length === 0 ? (
            <p className="font-serif text-cream-soft text-sm">
              No commissionable sessions booked for {studioName} today.
            </p>
          ) : null}
          {cover.map((c, idx) => {
            const p = payouts.find((x) => x.artistId === c.artistId);
            if (!p) return null;
            const st = payoutState(p, committedByArtist.get(p.artistId) ?? []);
            const canSend = p.payable && p.outstandingAud > 0 && p.record?.status !== 'sent';
            const firstName = p.artistName.split(' ')[0] ?? p.artistName;
            const clockOff = c.shiftEndIso ? finishTime(c.shiftEndIso) : 'in chair';
            return (
              <Fragment key={p.artistId}>
                {idx === firstClampedIdx ? (
                  <div data-testid="payout-6pm-divider" className="pt-1.5">
                    <div className="flex items-center gap-2.5">
                      <span className="heading-serif text-cream text-sm">6:00 PM · clock-off</span>
                      <span className="flex-1 h-px bg-gold-dim/40" />
                    </div>
                    <p className="font-inter text-[11px] text-cream-soft mt-1.5 mb-0.5">
                      Every remaining client pays their balance now, so the till catches up and everyone
                      still here is settled together.
                    </p>
                  </div>
                ) : null}
                <div
                  className={clsx(
                    'panel-dark p-3 border',
                    c.covered ? 'border-mint/40 bg-mint/5' : 'border-blood/45 bg-blood/5',
                  )}
                  data-testid={`payout-row-${p.artistId}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-serif text-cream">
                        {p.artistName}{' '}
                        <span className="text-cream-faint text-sm">
                          · {p.clampedToClockoff ? 'evening' : `clocks off ${clockOff}`} · {arrangementLabel(p.arrangement)}
                        </span>
                      </p>
                      <p className="font-mono text-[11px] text-cream-faint mt-0.5">
                        {aud(p.takingsAud)} takings · {p.sessionCount} session{p.sessionCount === 1 ? '' : 's'}
                        {p.arrangement.kind === 'percentage' ? ` · merch excluded` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="heading-serif text-lg text-cream">{aud(p.envelopeAud)}</p>
                      <span className={clsx('text-[10px] mt-1 inline-block', st.pill)}>{st.label}</span>
                    </div>
                  </div>

                  {/* What this artist is owed from EARLIER days, whole. Above the
                      cover box because it is the first thing that changes what the
                      manager owes them today. */}
                  <OutstandingBlock
                    studioId={studioId}
                    dayIso={dayIso}
                    artistId={p.artistId}
                    todayAud={p.outstandingAud}
                  />

                  {/* Cash cover, forecast vs forecast: the day's EXPECTED cash by
                      this artist's clock-off (actual so far + balances still to
                      land) against the cumulative envelopes owed to here. Red means
                      a genuine gap the day's own takings will not close even by 6pm,
                      not an intraday timing artefact. Actual cash in the till so far
                      rides along as an informational note; it never flips the colour. */}
                  <div
                    className={clsx(
                      'mt-2.5 rounded-md border px-2.5 py-1.5',
                      c.covered ? 'border-mint/30' : 'border-blood/35',
                    )}
                    data-testid={`cover-${p.artistId}`}
                    data-covered={String(c.covered)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                        Cash by {c.shiftEndIso ? finishTime(c.shiftEndIso) : 'clock-off'}
                      </span>
                      <span className={clsx('text-[10px]', c.covered ? 'pill-mint' : 'pill-blood')}>
                        {c.covered ? 'Covered' : `Short ${aud(c.shortfallAud)}`}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-0.5 font-mono text-[11px]">
                      <span className={c.covered ? 'text-mint' : 'text-blood'}>Expected in {aud(c.expectedByShiftEndAud)}</span>
                      <span className="text-cream-soft">Owed to here {aud(c.cumulativeDueAud)}</span>
                      <span className="text-cream-faint">In till now {aud(c.cashInHandAud)}</span>
                    </div>
                    {!c.covered ? (
                      <p className="font-serif text-cream-soft text-[11.5px] mt-1.5" data-testid={`cover-read-${p.artistId}`}>
                        By {clockOff} the day is expected to have taken {aud(c.expectedByShiftEndAud)} against{' '}
                        {aud(c.cumulativeDueAud)} owed to {firstName}, still {aud(c.shortfallAud)} short once every balance
                        lands. Bank an early cash sale to close it.
                      </p>
                    ) : null}
                  </div>
                  {canSend ? (
                    <SendControls
                      studioId={studioId}
                      dayIso={dayIso}
                      artistId={p.artistId}
                      firstName={firstName}
                      bundle={bundleFor(p.artistId)}
                    />
                  ) : null}
                </div>
              </Fragment>
            );
          })}
        </div>

        {/* Cash ledger */}
        <div className="panel p-4 lg:col-span-2" data-testid="cash-ledger-panel">
          <p className="label-small-caps text-gold-bright">Cash ledger · {studioName} · today</p>
          <table className="w-full text-sm mt-3">
            <tbody>
              <tr>
                <td className="py-1 text-cream-soft">Cash taken (make-sales)</td>
                <td className="py-1 text-right font-mono text-mint">+{aud(ledger.cashInAud)}</td>
              </tr>
              <tr>
                <td className="py-1 text-cream-soft">Payouts handed out</td>
                <td className="py-1 text-right font-mono text-blood">−{aud(ledger.payoutsAud)}</td>
              </tr>
              <tr className="border-t-2 border-gold-dim/40">
                <td className="pt-3 text-cream font-semibold">
                  Working cash{' '}
                  <span className="font-normal text-cream-faint text-xs">· steering reads this</span>
                </td>
                <td className="pt-3 text-right font-mono font-bold text-cream text-base">{aud(working)}</td>
              </tr>
              <tr>
                <td className="pt-3 text-cream-faint text-xs">
                  Float balance{' '}
                  <span className="pill-cream text-[9px] ml-1">sealed · emergency only</span>
                </td>
                <td className="pt-3 text-right">
                  <span className="inline-flex items-center gap-2 justify-end">
                    <span className="font-mono text-cream-faint text-xs">{aud(ledger.openingFloatAud)}</span>
                    <button
                      type="button"
                      onClick={() => setFloatOpen((v) => !v)}
                      className="btn-quiet text-[10px] px-1.5 py-0.5"
                      data-testid="float-manage"
                      aria-expanded={floatOpen}
                    >
                      {floatOpen ? 'Close' : 'Manage'}
                    </button>
                  </span>
                </td>
              </tr>
              <tr>
                <td className="text-cream-faint text-xs">Physical in till (float + working)</td>
                <td className="text-right font-mono text-cream-faint text-xs">{aud(physicalInTill)}</td>
              </tr>
            </tbody>
          </table>

          {floatOpen ? (
            <div className="mt-3 rounded-md border border-gold-dim/25 p-3" data-testid="float-manage-panel">
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                Move cash in or out of the float
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="font-mono text-cream-faint text-xs">$</span>
                <input
                  type="number"
                  min="0"
                  step="10"
                  inputMode="decimal"
                  value={floatDraft}
                  onChange={(e) => setFloatDraft(e.target.value)}
                  placeholder="0"
                  aria-label="Float movement amount"
                  className="w-24 bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-right font-mono text-xs text-cream"
                  data-testid="float-amount"
                />
                <button
                  type="button"
                  onClick={() => moveFloat(1)}
                  disabled={!canMove}
                  className="btn-primary text-[10px] px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="float-infuse"
                >
                  + Add
                </button>
                <button
                  type="button"
                  onClick={() => moveFloat(-1)}
                  disabled={!canMove}
                  className="btn-quiet text-[10px] px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="float-withdraw"
                >
                  − Withdraw
                </button>
              </div>
              <input
                type="text"
                value={floatReason}
                onChange={(e) => setFloatReason(e.target.value)}
                placeholder="Reason (required for every float move - e.g. banked, supplies)"
                aria-label="Float movement reason"
                className="mt-2 block w-full bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-xs text-cream placeholder:text-cream-faint"
                data-testid="float-reason"
              />

              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mt-3 mb-1">
                Float ledger
              </p>
              {ledger.floatMovements.length === 0 ? (
                <p className="font-serif text-cream-faint text-xs">No float movements yet.</p>
              ) : (
                <ul className="space-y-1" data-testid="float-ledger">
                  {ledger.floatMovements.map((m) => (
                    <li key={m.id} className="flex items-start justify-between gap-2 text-xs">
                      <span className="text-cream-soft capitalize min-w-0">
                        {m.kind.replace('-', ' ')}
                        <span className="text-cream-faint font-mono ml-2">{finishTime(m.at)}</span>
                        {m.note ? (
                          <span className="block text-cream-faint text-[11px] normal-case truncate">
                            {m.note}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={clsx('font-mono shrink-0', m.amountAud >= 0 ? 'text-mint' : 'text-blood')}
                      >
                        {m.amountAud >= 0 ? '+' : '−'}
                        {aud(Math.abs(m.amountAud))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
          {ledger.floatDipped ? (
            <div className="mt-3 rounded-md border border-blood/45 bg-blood/5 p-2.5" data-testid="float-dipped-flag">
              <span className="pill-blood text-[10px]">Float broken</span>
              <p className="font-serif text-cream-soft text-xs mt-1.5">
                An emergency float dip was logged today — flagged red for back-office (E3/E4).
              </p>
            </div>
          ) : (
            <div className="mt-3 rounded-md border border-mint/40 bg-mint/5 p-2.5">
              <span className="pill-mint text-[10px]">Float sealed</span>
              <p className="font-serif text-cream-soft text-xs mt-1.5">
                Not counted for steering. Breaking it flags red for E3/E4.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What an artist is owed from EARLIER days, one row per whole work day with its
 * age, plus what that makes the total to settle. Nothing here is a remainder: an
 * artist is paid whole or not at all, so every row is a full envelope waiting.
 *
 * Reads `artistOutstanding` — the same read the artist's own Earnings tab uses, so
 * the two screens cannot disagree about the debt.
 */
function OutstandingBlock({
  studioId,
  dayIso,
  artistId,
  todayAud,
}: {
  studioId: StudioId;
  dayIso: string;
  artistId: string;
  todayAud: number;
}) {
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const owed = useMemo(
    () => artistOutstanding(studioId, artistId, dayIso).filter((d) => d.dayIso < dayKey(dayIso)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, artistId, dayIso, payoutRecords],
  );
  if (owed.length === 0) return null;

  const arrearsAud = owed.reduce((sum, d) => sum + d.envelopeAud, 0);
  const escalated = owed.some((d) => d.escalated);
  return (
    <div
      className={clsx(
        'mt-2.5 rounded-md border px-2.5 py-1.5',
        escalated ? 'border-blood/55 bg-blood/10' : 'border-gold-dim/30',
      )}
      data-testid={`outstanding-${artistId}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
          Owed from earlier days
        </span>
        <span className={clsx('text-[10px]', escalated ? 'pill-blood' : 'pill-cream')}>
          {escalated ? `Owed ${owed[0]!.ageDays} days` : `${owed.length} day${owed.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <div className="mt-1 space-y-0.5">
        {owed.map((d) => (
          <div
            key={d.dayIso}
            className="flex items-baseline justify-between gap-3 font-mono text-[11px]"
            data-testid={`outstanding-${artistId}-${d.dayIso}`}
          >
            <span className="text-cream-soft">
              {format(new Date(`${d.dayIso}T12:00:00+10:00`), 'EEE d MMM')}
              <span className={clsx('ml-2', d.escalated ? 'text-blood' : 'text-cream-faint')}>
                {d.ageDays} day{d.ageDays === 1 ? '' : 's'}
              </span>
            </span>
            <span className="text-cream">{aud(d.envelopeAud)}</span>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 font-mono text-[11px] pt-1 border-t border-gold-dim/20">
          <span className="text-cream">To settle</span>
          <span className="text-cream font-semibold" data-testid={`to-settle-${artistId}`}>
            {aud(arrearsAud + todayAud)}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The send action, gated on whole days. Cash covers the queue oldest first, so the
 * button says exactly which days it settles; when the drawer cannot cover the next
 * whole day the manager gets the two honest choices instead — break the sealed
 * float (logged, flags the day) or let the day roll intact.
 */
function SendControls({
  studioId,
  dayIso,
  artistId,
  firstName,
  bundle,
}: {
  studioId: StudioId;
  dayIso: string;
  artistId: string;
  firstName: string;
  bundle: PayableBundle;
}) {
  const label = (dayIsos: string[]): string =>
    dayIsos.map((d) => format(new Date(`${d}T12:00:00+10:00`), 'EEE d')).join(' + ');
  const blocked = bundle.blockedDayIsos.length > 0;

  return (
    <div className="mt-2.5 space-y-1.5" data-testid={`send-controls-${artistId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          disabled={bundle.dayIsos.length === 0}
          onClick={() =>
            sendPayouts({ studioId, artistId, dayIsos: bundle.dayIsos, payDayIso: dayIso })
          }
          className="btn-primary text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`payout-send-${artistId}`}
        >
          Send {aud(bundle.totalAud)}
          {bundle.dayIsos.length > 0 ? ` · ${label(bundle.dayIsos)}` : ''}
        </button>
        {blocked ? (
          <>
            <button
              type="button"
              onClick={() =>
                settlePayouts({
                  studioId,
                  artistId,
                  dayIsos: [...bundle.dayIsos, ...bundle.blockedDayIsos],
                  payDayIso: dayIso,
                  signedByName: firstName,
                  useFloat: true,
                })
              }
              className="btn-ghost text-[11px] border-blood/55 text-blood hover:bg-blood/10"
              data-testid={`payout-float-dip-${artistId}`}
            >
              Break float · {aud(bundle.shortAud)}
            </button>
            <button
              type="button"
              onClick={() =>
                bundle.blockedDayIsos.forEach((day) =>
                  deferPayout({ studioId, artistId, dayIso: day, decidedBy: firstName }),
                )
              }
              className="btn-quiet text-[10px]"
              data-testid={`payout-defer-${artistId}`}
            >
              Defer to tomorrow
            </button>
          </>
        ) : null}
      </div>
      {blocked ? (
        <p className="font-serif text-cream-soft text-[11.5px]" data-testid={`send-blocked-${artistId}`}>
          Cannot part-pay {firstName}. {aud(bundle.shortAud)} short of{' '}
          {label(bundle.blockedDayIsos)}
          {bundle.dayIsos.length > 0 ? ', so only the days above can go now' : ''}. Break the sealed float
          to pay in full, or let it roll whole to tomorrow.
        </p>
      ) : null}
    </div>
  );
}

/**
 * One cell of the day-at-a-glance summary strip. Every tile is a disclosure:
 * clicking it opens its own derivation below the strip, so no figure on this
 * screen is a number the reader has to take on trust.
 */
function SummaryTile({
  label,
  value,
  note,
  tone = 'text-gold-bright',
  open,
  onToggle,
  testId,
}: {
  label: string;
  value: string;
  note: string;
  tone?: string;
  open: boolean;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={testId}
      className={clsx(
        'bg-ink-deep p-3 text-left w-full transition hover:bg-ink-soft/60',
        open && 'bg-ink-soft/70',
      )}
    >
      <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">{label}</p>
      <p className={clsx('font-cinzel text-2xl mt-1', tone)}>{value}</p>
      <p className="font-inter text-[11px] text-cream-soft mt-0.5">
        {note} <span className="text-gold-bright">{open ? '▾' : '▸'}</span>
      </p>
    </button>
  );
}
